import React, { useState, useEffect } from "react";
import styled from "styled-components";
import sanityClient from "../sanityClient";
import { Link } from "react-router-dom"; // Import Link from react-router-dom

const SponsorCarousel = () => {
  const [wholeHouseSponsors, setWholeHouseSponsors] = useState([]);
  const [halfHouseSponsors, setHalfHouseSponsors] = useState([]);
  const [otherSponsors, setOtherSponsors] = useState([]);
  const [index, setIndex] = useState(0);

  // Fisher-Yates shuffle function to randomize the array
  const shuffleArray = (array) => {
    let shuffledArray = array.slice(); // Copy the array
    for (let i = shuffledArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledArray[i], shuffledArray[j]] = [
        shuffledArray[j],
        shuffledArray[i],
      ];
    }
    return shuffledArray;
  };

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "sponsor" && defined(tier) && defined(logo.asset._ref)]{
          _id,
          name,
          "imageUrl": logo.asset->url,
          tier
        }`
      )
      .then((data) => {
        const uniqueSponsors = Array.from(
          new Set(data.map((sponsor) => sponsor._id))
        ).map((id) => data.find((sponsor) => sponsor._id === id));

        // Separate sponsors by tier
        const wholeHouse = uniqueSponsors.filter(
          (sponsor) => sponsor.tier === "wholeHouse"
        );
        const halfHouse = uniqueSponsors.filter(
          (sponsor) => sponsor.tier === "halfHouse"
        );
        const others = uniqueSponsors.filter(
          (sponsor) => sponsor.tier === "other"
        );

        // Shuffle each array to randomize the order
        setWholeHouseSponsors(shuffleArray(wholeHouse));
        setHalfHouseSponsors(shuffleArray(halfHouse));
        setOtherSponsors(shuffleArray(others));
      })
      .catch(console.error);
  }, []);

  const nextSlide = () => {
    setIndex((prevIndex) => (prevIndex + 1) % otherSponsors.length);
  };

  const prevSlide = () => {
    setIndex((prevIndex) =>
      prevIndex === 0 ? otherSponsors.length - 1 : prevIndex - 1
    );
  };

  useEffect(() => {
    const interval = setInterval(nextSlide, 6000); // Double the time for the transition
    return () => clearInterval(interval);
  }, [otherSponsors.length]);

  return (
    <FullWidthContainer>
      <Container>
        <Title>Special Thanks To Our Sponsors and Partners</Title>
        <StaticSponsors>
          <SponsorGrid columns={2}>
            {wholeHouseSponsors.map((sponsor) => (
              <BigSponsorItem key={sponsor._id}>
                <Link to={`/sponsors/${sponsor._id}`}>
                  <SponsorLogo src={sponsor.imageUrl} alt={sponsor.name} />
                </Link>
              </BigSponsorItem>
            ))}
          </SponsorGrid>
          <SponsorGrid columns={4}>
            {halfHouseSponsors.map((sponsor) => (
              <MediumSponsorItem key={sponsor._id}>
                <Link to={`/sponsors/${sponsor._id}`}>
                  <SponsorLogo src={sponsor.imageUrl} alt={sponsor.name} />
                </Link>
              </MediumSponsorItem>
            ))}
          </SponsorGrid>
        </StaticSponsors>
        <CarouselContainer>
          <Arrow onClick={prevSlide}>&lt;</Arrow>
          <CarouselWrapper index={index} length={otherSponsors.length}>
            {otherSponsors.map((sponsor, idx) => (
              <Slide key={idx}>
                <SmallSponsorLogo src={sponsor.imageUrl} alt={sponsor.name} />
              </Slide>
            ))}
          </CarouselWrapper>
          <Arrow onClick={nextSlide}>&gt;</Arrow>
        </CarouselContainer>
        <TextCTA>
          Interested in joining this esteemed list of sponsors and helping us
          fight Buffalo's affordable housing crisis?{" "}
          <Link to="/get-involved/team-build-opportunities">
            Learn how you can get involved.
          </Link>
        </TextCTA>
      </Container>
    </FullWidthContainer>
  );
};

// Styled components
const FullWidthContainer = styled.div`
  background-color: white; /* Ensure full width background color */
  padding: 20px 0;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
  background-color: white; /* Inner container also has a white background */

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const Title = styled.h2`
  font-size: var(--font-size-heading);
  font-weight: 700;
  margin-bottom: 20px;
`;

const StaticSponsors = styled.div`
  margin-bottom: 20px;
`;

const SponsorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns}, 1fr);
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: ${(props) =>
      props.columns === 2
        ? "1fr"
        : "1fr 1fr"}; /* Large logos 1 per row, Medium 2 per row on mobile */
    gap: 10px; /* Reduced gap on mobile for a tighter layout */
  }
`;

const BigSponsorItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    grid-column: span 2; /* Full-width on mobile */
  }
`;

const MediumSponsorItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SponsorLogo = styled.img`
  width: auto;
  height: 120px; /* Increased size */
  max-width: 100%;
  object-fit: contain;
  transition: transform 0.3s ease, box-shadow 0.3s ease; /* Smooth transition for hover effect */

  &:hover {
    transform: scale(1.05); /* Slightly increase the size */
    box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.2); /* Add shadow */
  }

  @media (max-width: 768px) {
    height: 100px; /* Smaller height on mobile */
  }
`;

const CarouselContainer = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  position: relative;
  width: 70%; /* Tighten the width on desktop */
  margin: 0 auto;

  @media (max-width: 768px) {
    width: 90%; /* Wider width on mobile */
  }
`;

const Arrow = styled.div`
  font-size: 2rem;
  cursor: pointer;
  user-select: none;
  z-index: 1;
  color: var(--hhb-primary);

  &:first-child {
    position: absolute;
    left: 10px;
  }

  &:last-child {
    position: absolute;
    right: 10px;
  }
`;

const CarouselWrapper = styled.div`
  display: flex;
  transition: transform 1s ease-in-out;
  transform: ${({ index, length }) =>
    `translateX(-${
      (index % length) * (100 / 5)
    }%)`}; /* Adjusted for tighter spacing */
  width: ${({ length }) =>
    `${length * (100 / 5)}%`}; /* Adjusted for tighter spacing */

  @media (max-width: 768px) {
    transform: ${({ index, length }) =>
      `translateX(-${
        (index % length) * (100 / 2)
      }%)`}; /* Two logos per slide on mobile */
    width: ${({ length }) => `${length * (100 / 2)}%`};
  }
`;

const Slide = styled.div`
  min-width: calc(100% / 5); /* Show 5 logos per slide on desktop */
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: auto;
    height: 90px; /* Increased size for carousel logos */
    object-fit: contain;
  }

  @media (max-width: 768px) {
    min-width: calc(100% / 2); /* Show 2 logos per slide on mobile */
    img {
      height: 80px; /* Smaller height for a compact layout */
    }
  }
`;

const SmallSponsorLogo = styled.img`
  width: auto;
  height: 90px; /* Increased size */
  max-width: 100%;
  object-fit: contain;
  transition: transform 0.3s ease, box-shadow 0.3s ease; /* Smooth transition for hover effect */

  &:hover {
    transform: scale(1.05); /* Slightly increase the size */
    box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.2); /* Add shadow */
  }

  @media (max-width: 768px) {
    height: 80px; /* Slightly reduced size for mobile */
  }
`;

const TextCTA = styled.p`
  text-align: center;
  margin-top: 40px;
  font-size: 1.2em;

  a {
    color: var(--hhb-primary);
    text-decoration: underline;

    &:hover {
      color: var(--hhb-darkblue);
    }
  }

  @media (max-width: 768px) {
    font-size: 1.1em;
    margin-top: 30px;
  }

  @media (max-width: 480px) {
    font-size: 1em;
    margin-top: 20px;
  }
`;

export default SponsorCarousel;
